import {
    EntityJson,
    PolicyId,
    StaticPolicySet,
    Template,
    TemplateLink
} from "@cedar-policy/cedar-wasm";
import {
    PermissionEntityType,
    PermissionResourceType,
    PermissionType,
    PolicyTemplateLink,
    Team,
} from "../../../../models";
import { TypeAndId, isAuthorized } from "@cedar-policy/cedar-wasm/web";
import { useEffect, useMemo, useState } from 'react';

import { dataStorePolicyTemplateLinkDAO } from "../../dao/DataStorePolicyTemplateLinkDAO";
import { dataStoreTeamDAO } from "../../../team/dao/DataStoreTeamDAO";
import { dataStoreTeamMemberDAO } from "../../../teamMember/dao/DataStoreTeamMemberDAO";
import { defaultPolicyTemplateStore } from "../DefaultPolicyTemplateStore";
import { defaultStaticPolicyStore } from "../DefaultStaticPolicyStore";
import { initCedar } from "../initCedar";
import { resourceTypeToEntityResolverMap } from "../entityResolvers/ResourceToEntityResolverMap";
import { schema } from "../Schema";
import { useQuery } from "react-query";
import { useUser } from "../../../../components/user/UserContext";

export const POLICY_TEMPLATE_LINKS_QUERY_KEY = "policyTemplateLinks";

export const usePermissionCheck = () => {
    const [isInitialized, setIsInitialized] = useState(false);

    useEffect(() => {
        initCedar().then(setIsInitialized);
    }, []);

    const { user } = useUser();
    const { data: teams } = useQuery<Team[]>({
        queryKey: ["teams"],
        queryFn: async () => {
            const teamMembers = await dataStoreTeamMemberDAO.listByUserId(user.getUsername());
            const distinctTeamIds = new Set(teamMembers.map(teamMember => teamMember.teamId));
            const teams = (await Promise.all(
                Array.from(distinctTeamIds)
                    .map(teamId => dataStoreTeamDAO.getById(teamId))
                    .filter(team => team != null)
            )) as Team[];
            return teams;
        },
        enabled: !!user
    });

    const { data: policyTemplateLinks } = useQuery<PolicyTemplateLink[]>({
        queryKey: [POLICY_TEMPLATE_LINKS_QUERY_KEY],
        queryFn: async () => {
            const entities: Array<TypeAndId> = [
                {
                    id: user.getUsername(),
                    type: PermissionEntityType.USER
                },
                ...(teams ?? []).map(team => ({
                    id: team.id,
                    type: PermissionEntityType.TEAM
                }))
            ];
            return (await Promise.all(
                entities.map(entity => dataStorePolicyTemplateLinkDAO.listByEntityTypeAndId(entity.type as PermissionEntityType, entity.id))
            )).flat() as PolicyTemplateLink[];
        },
        enabled: !!user && !!teams
    });

    const { data: staticPolicies } = useQuery<StaticPolicySet>({
        queryKey: ["staticPolicies"],
        queryFn: async () => {
            return await defaultStaticPolicyStore.getStaticPolicies();
        }
    });

    const { data: templates } = useQuery<Record<PolicyId, Template>>({
        queryKey: ["templates"],
        queryFn: async () => {
            return await defaultPolicyTemplateStore.listAll();
        }
    });

    const checkPermission = async (resourceId: string, resourceType: PermissionResourceType, permissionType: PermissionType) => {
        try {
            console.debug(`Checking permission for resourceId: ${resourceId}, resourceType: ${resourceType}, permissionType: ${permissionType}`);
            const userPrincipal: EntityJson = {
                uid: {
                    id: user.getUsername(),
                    type: PermissionEntityType.USER
                },
                attrs: {},
                parents: [
                    ...(teams ?? []).map(team => ({
                        __entity: {
                            id: team.id,
                            type: PermissionEntityType.TEAM
                        }
                    }))
                ]
            };
            const resourceEntity: EntityJson | undefined = await resourceTypeToEntityResolverMap.get(resourceType)?.resolveEntity(resourceType, resourceId);
            if (!resourceEntity) {
                throw new Error(`No entity resolver found for resource type: ${resourceType}`);
            }
            const templateLinks: TemplateLink[] = (policyTemplateLinks ?? []).map(link => ({
                templateId: link.templateId,
                newId: link.id,
                values: {
                    "?principal": {
                        id: link.entityId,
                        type: link.entityType
                    },
                    "?resource": {
                        id: link.resourceId,
                        type: link.resourceType
                    }
                }
            }));
            const authInput = {
                action: {
                    id: permissionType,
                    type: "Action"
                },
                entities: [userPrincipal, resourceEntity],
                principal: {
                    id: user.getUsername(),
                    type: PermissionEntityType.USER
                },
                resource: {
                    id: resourceId,
                    type: resourceType
                },
                context: {},
                policies: {
                    staticPolicies,
                    templates,
                    templateLinks
                },
                schema: schema
            };
            console.debug("Permission check input: ", authInput);
            const authResult = isAuthorized(authInput);
            console.debug("Permission check result: ", authResult);
            return authResult.type === "success" && authResult.response.decision === "allow";
        } catch (error) {
            console.error("Error checking permission: ", error);
            return false;
        }
    };

    const isReady = useMemo(() => {
        return isInitialized && !!user && !!teams && !!policyTemplateLinks && !!staticPolicies && !!templates;
    }, [isInitialized, user, teams, policyTemplateLinks, staticPolicies, templates]);

    return {
        isReady,
        checkPermission
    };
};